import React, { useState, useEffect, useCallback } from 'react';
import APIService from '../services/api';

/**
 * Qui est prévenu pour CE besoin, côté Sourceur.
 *
 * Deux endroits, une seule liste :
 *  - un bandeau en haut de la campagne : les personnes prévenues pour ce
 *    besoin. C'est la valeur par défaut, elle se règle une fois ;
 *  - au lancement d'une recherche : la même liste, pré-cochée, que l'on
 *    peut ajuster POUR CETTE CAMPAGNE sans toucher au défaut du besoin.
 *
 * Ce réglage ne remplace pas les règles de notification (Configuration →
 * Notifications) : il dit QUI, elles disent QUOI et COMMENT. Les personnes
 * choisies ici s'ajoutent aux destinataires des règles Sourcing.
 *
 * Routes : GET/PUT /besoins/:id/sourcing/notifies → { user_ids: [] }
 * Le lancement transmet `notifier_user_ids` dans POST /besoins/:id/sourcing.
 * Routes absentes : l'écran le dit, n'écrit rien, et la recherche part
 * quand même — une notification non réglée ne bloque pas un sourcing.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const nomDe = (u) => [u?.prenom, u?.nom].filter(Boolean).join(' ').trim()
  || u?.full_name || u?.name || u?.username || u?.email || `Utilisateur ${u?.id ?? ''}`.trim();
const memeSet = (a, b) => a.length === b.length && a.every(x => b.some(y => String(y) === String(x)));

// Un compte RESSOURCE (consultant qui saisit son CRA) n'est pas un
// utilisateur interne : il n'a rien à faire dans « qui prévenir » — même
// critère que la Configuration pour ses « internes ».
const estInterne = (u) => !(u.role === 'ressource' || u.ressource === true);

/** Les comptes INTERNES, avec le même repli que la configuration. */
export function useUtilisateurs() {
  const [users, setUsers] = useState(null); // null = chargement
  useEffect(() => {
    let annule = false;
    (async () => {
      for (const url of [`${BASE}/config/users/list`, `${BASE}/users`]) {
        try {
          const r = await fetch(url, { headers: authH() });
          if (!r.ok) continue;
          const j = await r.json().catch(() => null);
          const arr = j?.data || j?.users || (Array.isArray(j) ? j : []);
          const ok = Array.isArray(arr) ? arr.filter(u => u && u.id != null && estInterne(u)) : [];
          if (ok.length) { if (!annule) setUsers(ok); return; }
        } catch { /* on tente la route suivante */ }
      }
      if (!annule) setUsers([]);
    })();
    return () => { annule = true; };
  }, []);
  return users;
}

/** Destinataires par défaut du besoin. */
export function useNotifiesBesoin(besoinId) {
  const [ids, setIds] = useState(null);   // null = chargement
  const [absent, setAbsent] = useState(false);
  const charger = useCallback(async () => {
    if (besoinId == null) { setIds([]); return; }
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/sourcing/notifies`, { headers: authH() });
      if (r.status === 404 || r.status === 501) { setAbsent(true); setIds([]); return; }
      const j = await r.json().catch(() => ({}));
      const d = j.data ?? j;
      const arr = d?.user_ids ?? d?.users ?? [];
      setIds(Array.isArray(arr) ? arr.map(x => (x && x.id != null ? x.id : x)) : []);
    } catch { setIds([]); }
  }, [besoinId]);
  useEffect(() => { charger(); }, [charger]);
  return { ids, absent, recharger: charger };
}

/** Puces cochables. */
export function ChoixUtilisateurs({ users, valeur, onChange, verrouilles = [], titreVerrou }) {
  if (users === null) return <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement des comptes…</span>;
  if (!users.length) return <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Aucun compte à proposer.</span>;
  const estVerrouille = (id) => verrouilles.some(v => String(v) === String(id));
  const bascule = (id) => {
    if (estVerrouille(id)) return;   // toujours prévenu : le décocher serait faux
    const dedans = valeur.some(v => String(v) === String(id));
    onChange(dedans ? valeur.filter(v => String(v) !== String(id)) : [...valeur, id]);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {users.map(u => {
        const fige = estVerrouille(u.id);
        const on = fige || valeur.some(v => String(v) === String(u.id));
        return (
          <button key={u.id} type="button" onClick={() => bascule(u.id)}
            title={fige ? (titreVerrou || 'Toujours prévenu.') : (u.email || undefined)}
            className={on ? undefined : 'src-row-hover'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 11px', borderRadius: '99px', cursor: fige ? 'default' : 'pointer', userSelect: 'none',
              fontFamily: 'inherit', fontSize: '12px', fontWeight: on ? 600 : 500,
              border: on ? '1px solid transparent' : '1px solid var(--input-border)',
              background: on ? 'var(--accent)' : 'var(--ghost-bg)',
              color: on ? 'var(--accent-fg)' : 'var(--text-2)' }}>
            <BI icon={fige ? 'lock-fill' : on ? 'check-lg' : 'plus-lg'} style={{ fontSize: '10px', opacity: on ? 1 : 0.6 }} />
            {nomDe(u)}{fige ? ' (vous)' : ''}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Bandeau en tête de campagne : le réglage par défaut du besoin.
 */
export function BandeauNotifiesBesoin({ besoinId, showNotification }) {
  const users = useUtilisateurs();
  const { ids, absent, recharger } = useNotifiesBesoin(besoinId);
  const [sel, setSel] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (Array.isArray(ids)) setSel(ids); }, [ids]);

  const enregistrer = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/sourcing/notifies`, {
        method: 'PUT', headers: jsonH(), body: JSON.stringify({ user_ids: sel || [] }),
      });
      if (r.status === 404 || r.status === 501) { showNotification?.('L\'enregistrement des personnes prévenues arrive côté serveur — rien n\'a été enregistré.', 'error'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      // Le serveur ne conserve que les comptes existants de l'organisation :
      // c'est SA réponse qui fait foi, pas ce qu'on lui a envoyé.
      const d = j.data ?? j;
      const retour = Array.isArray(d?.user_ids) ? d.user_ids.map(x => (x && x.id != null ? x.id : x)) : null;
      if (retour) setSel(retour);
      showNotification?.(j.message || 'Personnes prévenues enregistrées pour ce besoin.', 'success');
      recharger();
      setOuvert(false);
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const nomsDe = (liste) => (users || []).filter(u => liste.some(v => String(v) === String(u.id))).map(nomDe);
  const modifie = Array.isArray(ids) && Array.isArray(sel) && !memeSet(ids, sel);
  const noms = Array.isArray(sel) ? nomsDe(sel) : [];

  return (
    <div style={{ marginTop: '14px', padding: '11px 14px', borderRadius: '11px', border: '1px solid var(--border)', background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <BI icon="bell" style={{ color: 'var(--text-3)', flex: 'none' }} />
        <span style={{ fontSize: '12.5px', color: 'var(--text-2)', flex: 1, minWidth: '200px' }}>
          {sel === null ? 'Lecture des personnes prévenues…'
            : noms.length === 0 ? 'Personne n\'est prévenu pour ce besoin.'
              : <>Prévenus pour ce besoin : <strong style={{ color: 'var(--text-strong)' }}>{noms.join(', ')}</strong></>}
        </span>
        <button onClick={() => setOuvert(o => !o)} className="src-row-hover"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flex: 'none' }}>
          <BI icon={ouvert ? 'chevron-up' : 'pencil'} style={{ fontSize: '10px' }} /> {ouvert ? 'Fermer' : 'Modifier'}
        </button>
      </div>

      {ouvert && (
        <div style={{ marginTop: '11px', paddingTop: '11px', borderTop: '1px solid var(--hair)' }}>
          <ChoixUtilisateurs users={users} valeur={sel || []} onChange={setSel} />
          <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: '220px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55 }}>
              Prévenus quand des profils sont trouvés et vont être contactés, et quand un numéro de
              téléphone est récupéré. Ces personnes sont pré-cochées au lancement de chaque campagne.
              {' '}Elles seront prévenues dans l'application ; pour recevoir aussi un email, activez une
              règle Sourcing dans Configuration → Notifications.
              {absent && ' L\'enregistrement arrive côté serveur.'}
            </span>
            <button onClick={enregistrer} disabled={busy || !modifie}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '7px 13px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: modifie ? 'pointer' : 'not-allowed', opacity: busy || !modifie ? 0.5 : 1, flex: 'none' }}>
              <BI icon="check-lg" /> {busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Bloc du formulaire de lancement : garder le défaut du besoin, ou ajuster
 * pour cette campagne. L'écart avec le défaut est affiché, pour qu'on sache
 * toujours si l'on part du réglage du besoin ou d'un choix ponctuel.
 */
export function NotifiesLancement({ defauts, absent, valeur, onChange }) {
  const users = useUtilisateurs();
  // Celui qui lance la recherche est prévenu d'office : le serveur l'ajoute
  // comme créateur de la campagne. On l'affiche donc coché et non
  // décochable — le décocher afficherait un état que le serveur dément.
  const moi = APIService.getCurrentUser();
  const moiId = moi?.id ?? null;
  const [ouvert, setOuvert] = useState(false);
  const base = Array.isArray(defauts) ? defauts : [];
  const identique = memeSet(base, valeur);
  const nomsDe = (liste) => (users || []).filter(u => liste.some(v => String(v) === String(u.id))).map(nomDe);
  const noms = nomsDe(moiId != null && !valeur.some(v => String(v) === String(moiId)) ? [...valeur, moiId] : valeur);
  const ajoutes = nomsDe(valeur.filter(v => !base.some(b => String(b) === String(v))));
  const retires = nomsDe(base.filter(b => !valeur.some(v => String(v) === String(b))));

  return (
    <div style={{ marginTop: '13px' }}>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '6px' }}>
        Qui prévenir
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: '210px', fontSize: '12.5px', color: 'var(--text-2)' }}>
          {noms.length === 0 ? 'Personne ne sera prévenu pour cette campagne.' : noms.join(', ')}
        </span>
        {identique
          ? <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-2)', flex: 'none' }}>réglage du besoin</span>
          : <span title="Ce choix ne vaut que pour cette campagne ; le réglage du besoin n'est pas modifié."
              style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: 'rgba(217,119,6,0.13)', color: '#b45309', flex: 'none' }}>ajusté pour cette campagne</span>}
        <button type="button" onClick={() => setOuvert(o => !o)} className="src-row-hover"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flex: 'none' }}>
          <BI icon={ouvert ? 'chevron-up' : 'people'} style={{ fontSize: '10px' }} /> {ouvert ? 'Fermer' : 'Ajouter ou retirer'}
        </button>
      </div>

      {ouvert && (
        <div style={{ marginTop: '10px' }}>
          <ChoixUtilisateurs users={users} valeur={valeur} onChange={onChange}
            verrouilles={moiId != null ? [moiId] : []}
            titreVerrou="Vous lancez la recherche : vous êtes prévenu d'office." />
          {!identique && (
            <div style={{ marginTop: '7px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ flex: 1, minWidth: '200px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                {[ajoutes.length ? `Ajoutés : ${ajoutes.join(', ')}` : null,
                  retires.length ? `Retirés : ${retires.join(', ')}` : null].filter(Boolean).join(' · ')}
              </span>
              <button type="button" onClick={() => onChange(base)} className="src-row-hover"
                style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' }}>
                Revenir au réglage du besoin
              </button>
            </div>
          )}
        </div>
      )}
      <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55 }}>
        Prévenus quand des profils sont trouvés et vont être contactés, et quand un numéro de
        téléphone est récupéré — dans l'application ; l'email suppose une règle Sourcing active
        (Configuration → Notifications).
        {moiId != null ? ' Vous lancez la recherche : vous êtes prévenu d\'office, sans avoir à vous cocher.' : ''}
        {absent ? ' Le réglage par défaut du besoin arrive côté serveur — cochez ici en attendant.' : ''}
      </div>
    </div>
  );
}

export default BandeauNotifiesBesoin;
